import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Stack,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import copyFill from "@iconify/icons-eva/copy-fill";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import CustomMUITable from "../../components/Table/CustomMUITable";
import Label from "../../components/Label";
import Page from "../../components/Page";
import FormDialog from "../../components/common/FormDialog";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DelegateForm from "./DelegateForm";
import DelegateDetails from "./DelegateDetails";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { usePGIMode } from "../../Hooks/PGIModeContext";

// ----------------------------------------------------------------------

import {
  createDelegate,
  listDelegates,
  updateDelegate,
  deleteDelegate,
} from "../../DAL/Delegate";
import { s3baseUrl, coachPortalUrl } from "../../config/config";

// ----------------------------------------------------------------------
const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export default function Delegates() {
  const classes = useStyles();
  const { metaTitle } = usePGIMode();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const getSearchFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("search") || "";
  };

  const [delegates, setDelegates] = useState([]);
  const [query, setQuery] = useState(getSearchFromUrl());
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [status, setStatus] = useState("all");

  const [formDialog, setFormDialog] = useState({ open: false, delegate: null });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    delegate: null,
  });
  const [changePasswordDialog, setChangePasswordDialog] = useState({
    open: false,
    delegate: null,
  });
  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    delegateId: null,
  });
  const [loading, setLoading] = useState(false);

  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [query]);

  const fetchDelegates = async () => {
    setIsLoading(true);
    try {
      const response = await listDelegates(page, rowsPerPage, {
        search_text: debouncedQuery,
        status: status,
      });
      if (response.code === 200) {
        setDelegates(response.coach_list || []);
        setTotalCount(response.total_coachs_count || 0);
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
      }
    } catch (error) {
      console.error("Error fetching Coachs:", error);
      enqueueSnackbar("Error fetching coachs", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDelegates();
  }, [page, rowsPerPage, debouncedQuery, status]);

  const handleChangeSearch = (e) => {
    setQuery(e.target.value);
    setPage(0);
  };

  const handleCreateDelegate = () => {
    setFormDialog({ open: true, delegate: null });
  };

  const handleEdit = (row) => {
    console.log(row, "Data");
    setFormDialog({ open: true, delegate: row });
  };

  const handleDelete = (row) => {
    setDeleteDialog({ open: true, delegate: row });
  };

  const handleChangePassword = (row) => {
    setChangePasswordDialog({ open: true, delegate: row });
  };

  const handleViewDetail = (row) => {
    setDetailsDialog({ open: true, delegateId: row._id });
  };

  const handleAddEditDelegate = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      const data = formDialog.delegate;
      console.log("Submitting data:", data);

      formData.append("first_name", data.first_name);
      formData.append("last_name", data.last_name || "");
      formData.append("email", data.email);
      if (data.password) formData.append("password", data.password);
      formData.append("contact_number", data.contact_number || "");
      formData.append("address", data.address || "");
      formData.append("city", data.city || "");
      formData.append("state", data.state || "");
      formData.append("main_heading", data.main_heading || "");
      formData.append("biography", data.biography || "");
      formData.append("status", data.status);
      formData.append("is_2f_enabled", data.is_2f_enabled);
      formData.append("time_zone", data.time_zone);

      if (data.image instanceof File) {
        console.log("New image selected, appending to FormData");
        formData.append("image", data.image);
      }
      let response;
      if (formDialog.delegate?._id) {
        response = await updateDelegate(formDialog.delegate._id, formData);
      } else {
        response = await createDelegate(formData);
      }

      if (response.code === 200) {
        enqueueSnackbar(response.message, { variant: "success" });

        if (formDialog.delegate?._id) {
          fetchDelegates();
        } else {
          // Update local state for add
          setDelegates((prev) => [response.coachUsers, ...prev]);
          setTotalCount((prev) => prev + 1);
        }

        setFormDialog({ open: false, delegate: null });
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
      }
    } catch (error) {
      console.error("Error saving coach:", error);
      enqueueSnackbar("Error saving coach", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDelegate = async () => {
    setLoading(true);

    try {
      const response = await deleteDelegate(deleteDialog.delegate._id);
      if (response.code === 200) {
        enqueueSnackbar(response.message, { variant: "success" });

        // Update local state for delete
        setDelegates((prev) =>
          prev.filter((item) => item._id !== deleteDialog.delegate._id)
        );
        setTotalCount((prev) => prev - 1);

        setDeleteDialog({ open: false, delegate: null });
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
      }
    } catch (error) {
      console.error("Error deleting coach:", error);
      enqueueSnackbar("Error deleting coach", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Transform delegates data to match table format
  const getTableData = () => {
    return delegates.map((delegate) => ({
      ...delegate,
      table_avatar: {
        src: delegate.image?.thumbnail_1
          ? s3baseUrl + delegate.image.thumbnail_1
          : null,
        alt: `${delegate.first_name} ${delegate.last_name}`,
      },
    }));
  };

  const TABLE_HEAD = [
    { id: "action", label: "Action", alignRight: false, type: "action" },
    { id: "number", label: "#", alignRight: false, type: "number" },
    {
      id: "profile",
      label: "Profile",
      alignRight: false,
      renderData: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={
              row.image?.thumbnail_1
                ? s3baseUrl + row.image.thumbnail_1
                : undefined
            }
            alt={`${row.first_name} ${row.last_name}`}
            sx={{ width: 50, height: 50 }}
            onClick={() => {
              if (row.image?.thumbnail_1) {
                window.open(
                  s3baseUrl + row.image.thumbnail_1,
                  "_blank",
                  "noopener,noreferrer"
                );
              }
            }}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {`${row.first_name} ${row.last_name}`}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                maxWidth: 200,
              }}
            >
              {row.biography || "N/A"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { id: "email", label: "Email", alignRight: false },
    {
      id: "contact_number",
      label: "Contact Number",
      alignRight: false,
      renderData: (row) => (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
            {row.contact_number || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "slug",
      label: "Login URL",
      alignRight: false,
      renderData: (row) => {
        const loginUrl = `${coachPortalUrl}/login/${row.slug}`;

        const handleCopy = (e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(loginUrl);
          enqueueSnackbar("Link copied to clipboard", { variant: "success" });
        };

        return (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            {/* <Typography
              variant="subtitle2"
              color={"primary.main"}
              sx={{ fontWeight: 400 }}
            >
              {row?.slug ? `/${row.slug}` : "-"}
            </Typography> */}
            {row?.slug && (
              <Tooltip title="Copy Link">
                <IconButton
                  size="medium"
                  onClick={handleCopy}
                  sx={{ color: "primary.main" }}
                >
                  <Icon icon={copyFill} width={24} height={24} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: "is_2f_enabled",
      label: "Two Factor Authentication",
      alignRight: false,
      renderData: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
          <Label
            variant="ghost"
            color={row.is_2f_enabled === true ? "success" : "error"}
          >
            {row.is_2f_enabled === true ? "Enabled" : "Disabled"}
          </Label>
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      alignRight: false,
      renderData: (row) => (
        <Label
          variant="ghost"
          color={row.status === true ? "success" : "error"}
        >
          {row.status === true ? "Active" : "Inactive"}
        </Label>
      ),
    },
  ];

  const MENU_OPTIONS = [
    {
      label: "View Detail",
      icon: "akar-icons:eye",
      handleClick: handleViewDetail,
    },
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleEdit,
    },
    {
      label: "Change Password",
      icon: "akar-icons:lock-on",
      handleClick: handleChangePassword,
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleDelete,
    },
  ];

  return (
    <Page title={`Coaches - ${metaTitle}`}>
      <Box sx={{ mx: "auto", px: 3, py: 4 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            Coaches
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <FormControl
              variant="outlined"
              sx={{
                minWidth: { xs: "100%", md: 250 },
                "& .MuiInputLabel-root": {
                  top: "50%",
                  transform: "translate(14px, -50%)",
                },
                "& .MuiInputLabel-shrink": {
                  top: 0,
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              }}
            >
              <InputLabel htmlFor="outlined-adornment-search">
                Search
              </InputLabel>

              <OutlinedInput
                id="outlined-adornment-search"
                value={query}
                onChange={handleChangeSearch}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
                label="Search"
                sx={{ height: 42 }}
              />
            </FormControl>
            {/* Status Filter */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <FormControl
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  minWidth: { md: 150 },
                  "& .MuiInputLabel-root": {
                    top: "50%",
                    transform: "translate(14px, -50%)",
                  },
                  "& .MuiInputLabel-shrink": {
                    top: 0,
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              >
                <InputLabel id="status-filter-label">
                  Filter By Status
                </InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(0);
                  }}
                  label="Filter By Status"
                  sx={{ height: 42 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            {/* ================= */}
            <Button
              onClick={handleCreateDelegate}
              variant="contained"
              size="large"
              startIcon={<Icon icon={plusFill} />}
              sx={{
                px: 3,
                width: { xs: "100%", sm: "auto" },
                height: 42,
              }}
            >
              Add New Coach
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ position: "relative" }}>
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.6)",
                zIndex: 10,
                borderRadius: 1,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
          <CustomMUITable
            TABLE_HEAD={TABLE_HEAD}
            MENU_OPTIONS={MENU_OPTIONS}
            data={getTableData()}
            pagePagination={true}
            is_hide={true}
            custom_pagination={{
              page: page,
              rows_per_page: rowsPerPage,
              total_count: totalCount,
              handleChangePage: (e, newPage) => setPage(newPage),
              onRowsPerPageChange: (e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              },
            }}
          />
        </Box>

        <FormDialog
          open={formDialog.open}
          onClose={() => setFormDialog({ open: false, delegate: null })}
          title={formDialog.delegate?._id ? "Edit Coach" : "Create Coach"}
          onSubmit={handleAddEditDelegate}
          submitText={formDialog.delegate?._id ? "Update" : "Add Coach"}
          loading={loading}
        >
          <DelegateForm
            delegate={formDialog.delegate}
            isEdit={!!formDialog.delegate?._id}
            onChange={(data) =>
              setFormDialog((prev) => ({ ...prev, delegate: data }))
            }
          />
        </FormDialog>

        <ChangePasswordDialog
          open={changePasswordDialog.open}
          onClose={() =>
            setChangePasswordDialog({ open: false, delegate: null })
          }
          delegate={changePasswordDialog.delegate}
        />

        <DelegateDetails
          open={detailsDialog.open}
          onClose={() => setDetailsDialog({ open: false, delegateId: null })}
          delegateId={detailsDialog.delegateId}
        />

        <ConfirmDialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, delegate: null })}
          onConfirm={handleDeleteDelegate}
          title="Delete Coach"
          message={`Are you sure you want to delete ${deleteDialog.delegate?.first_name} ${deleteDialog.delegate?.last_name}? This action cannot be undone.`}
          loading={loading}
        />
      </Box>
    </Page>
  );
}
